// src/components/layouts/Sidebar.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { CurrentUser } from "../../types";
import { MenuItem } from "../../types";
import { ChevronDown, Search, Wifi } from "../icons";

interface SidebarProps {
  isOpen: boolean;
  menuItems: MenuItem[];
  user: CurrentUser | null;
  onLogout?: () => void;
}

const GROUP_ACCENTS: Record<
  string,
  {
    activeBg: string;
    activeText: string;
    iconActiveBg: string;
    iconActiveText: string;
    dot: string;
    childActiveBg: string;
    childActiveText: string;
  }
> = {
  Overview: {
    activeBg:
      "bg-gradient-to-r from-emerald-500 to-teal-500 shadow-lg shadow-emerald-950/30",
    activeText: "text-white",
    iconActiveBg: "bg-white/20",
    iconActiveText: "text-white",
    dot: "bg-emerald-300",
    childActiveBg: "bg-white/10",
    childActiveText: "text-white",
  },
  Customers: {
    activeBg:
      "bg-gradient-to-r from-emerald-500 to-cyan-500 shadow-lg shadow-cyan-950/30",
    activeText: "text-white",
    iconActiveBg: "bg-white/20",
    iconActiveText: "text-white",
    dot: "bg-cyan-300",
    childActiveBg: "bg-white/10",
    childActiveText: "text-white",
  },
  Services: {
    activeBg:
      "bg-gradient-to-r from-blue-500 to-cyan-500 shadow-lg shadow-blue-950/30",
    activeText: "text-white",
    iconActiveBg: "bg-white/20",
    iconActiveText: "text-white",
    dot: "bg-blue-300",
    childActiveBg: "bg-white/10",
    childActiveText: "text-white",
  },
  Network: {
    activeBg:
      "bg-gradient-to-r from-amber-500 to-orange-500 shadow-lg shadow-amber-950/30",
    activeText: "text-white",
    iconActiveBg: "bg-white/20",
    iconActiveText: "text-white",
    dot: "bg-amber-300",
    childActiveBg: "bg-white/10",
    childActiveText: "text-white",
  },
  Finance: {
    activeBg:
      "bg-gradient-to-r from-teal-500 to-emerald-500 shadow-lg shadow-teal-950/30",
    activeText: "text-white",
    iconActiveBg: "bg-white/20",
    iconActiveText: "text-white",
    dot: "bg-teal-300",
    childActiveBg: "bg-white/10",
    childActiveText: "text-white",
  },
  Communication: {
    activeBg:
      "bg-gradient-to-r from-violet-500 to-indigo-500 shadow-lg shadow-indigo-950/30",
    activeText: "text-white",
    iconActiveBg: "bg-white/20",
    iconActiveText: "text-white",
    dot: "bg-violet-300",
    childActiveBg: "bg-white/10",
    childActiveText: "text-white",
  },
  General: {
    activeBg:
      "bg-gradient-to-r from-emerald-500 to-cyan-500 shadow-lg shadow-emerald-950/30",
    activeText: "text-white",
    iconActiveBg: "bg-white/20",
    iconActiveText: "text-white",
    dot: "bg-emerald-300",
    childActiveBg: "bg-white/10",
    childActiveText: "text-white",
  },
};

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  menuItems,
  user,
  onLogout,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [expandedItems, setExpandedItems] = useState<string[]>(["settings"]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showUserMenu, setShowUserMenu] = useState(false);

  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(e.target as Node)
      ) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filteredMenuItems = useMemo(() => {
    const userRole = localStorage.getItem("userRole");
    const userId = localStorage.getItem("userId");

    const hasPermission = (item: any) => {
      // Role check
      const roleAllowed =
        !item.allowedRoles ||
        (userRole && item.allowedRoles.includes(userRole));

      // User check
      const userAllowed =
        !item.allowedUsers || (userId && item.allowedUsers.includes(userId));

      return roleAllowed && userAllowed;
    };

    const filterTree = (items: any[]): any[] =>
      items.reduce((acc, item) => {
        // Block item if user has no permission
        if (!hasPermission(item)) return acc;

        // Handle children
        if (item.children) {
          const visibleChildren = filterTree(item.children);

          // Hide parent if no visible children
          if (visibleChildren.length === 0) return acc;

          return [
            ...acc,
            {
              ...item,
              children: visibleChildren,
            },
          ];
        }

        return [...acc, item];
      }, []);

    return filterTree(menuItems) as MenuItem[];
  }, []);

  const searchedMenuItems = useMemo(() => {
    if (!searchQuery.trim()) return filteredMenuItems;

    const q = searchQuery.toLowerCase();

    return filteredMenuItems
      .map((item) => {
        if (item.label.toLowerCase().includes(q)) return item;

        const childMatches = item.children?.filter((child) =>
          child.label.toLowerCase().includes(q),
        );

        if (childMatches?.length) {
          return { ...item, children: childMatches };
        }

        return null;
      })
      .filter(Boolean) as MenuItem[];
  }, [filteredMenuItems, searchQuery]);

  const groupedMenu = useMemo(() => {
    const groups: Record<string, MenuItem[]> = {};

    searchedMenuItems.forEach((item) => {
      const group = item.group || "General";
      if (!groups[group]) groups[group] = [];
      groups[group].push(item);
    });

    return groups;
  }, [searchedMenuItems]);

  const toggleExpand = (id: string) => {
    setExpandedItems((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleNavigation = (path?: string) => {
    if (path) navigate(path);
  };

  const isActiveRoute = (path?: string, children?: any[]) => {
    if (path && location.pathname === path) return true;
    return children?.some((child) => location.pathname === child.path) ?? false;
  };

  const userInitials = user
    ? (user.username?.[0] || user.email?.[0] || "U").toUpperCase()
    : "?";

  const userDisplayName = user?.username || user?.email || "Unknown";
  const userEmail = user?.email ?? "";

  return (
    <div
      className={`
        flex flex-col h-full
        bg-gradient-to-b from-slate-950 via-slate-950 to-slate-900
        border-r border-white/10
        transition-all duration-300 ease-in-out
        ${isOpen ? "w-[258px] min-w-[258px]" : "w-0 min-w-0"}
        overflow-hidden
      `}
    >
      {/* Brand */}
      <div className="flex items-center gap-3 px-5 pt-5 pb-4 shrink-0">
        <div
          className="
            flex h-11 w-11 shrink-0 items-center justify-center
            rounded-2xl
            bg-gradient-to-br from-emerald-400 to-cyan-500
            shadow-lg shadow-emerald-950/40
          "
        >
          <Wifi className="w-[20px] h-[20px] text-white" />
        </div>

        <div className="min-w-0">
          <p className="text-[15px] font-bold leading-tight text-white tracking-[-0.3px]">
            AAMX Billing
          </p>
          <p className="text-[11px] text-slate-400 leading-tight truncate mt-0.5">
            Network Control Center
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="px-4 pb-4 shrink-0">
        <div
          className="
            flex items-center gap-2
            rounded-2xl
            bg-white/5 border border-white/10
            px-3 h-10
            focus-within:border-emerald-400/60
            focus-within:ring-2 focus-within:ring-emerald-400/10
            transition-all
          "
        >
          <Search className="w-4 h-4 text-slate-500 shrink-0" />

          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search menu..."
            className="
              flex-1 bg-transparent border-none outline-none
              text-[13px] text-slate-200 placeholder:text-slate-500
            "
          />

          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="text-slate-500 hover:text-slate-300 text-[12px] leading-none"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav
        className="flex-1 overflow-y-auto px-3 pb-3 scrollbar-none"
        style={{ scrollbarWidth: "none" }}
      >
        {Object.entries(groupedMenu).map(([groupName, items]) => {
          const accent = GROUP_ACCENTS[groupName] ?? GROUP_ACCENTS["General"];

          return (
            <div key={groupName} className="mb-5">
              <p
                className="
                  px-2.5 mb-2
                  text-[10px] font-bold tracking-[0.08em]
                  uppercase text-slate-500 select-none
                "
              >
                {groupName}
              </p>

              <div className="space-y-1">
                {items.map((item) => {
                  const Icon = item.icon;
                  const isExpanded = expandedItems.includes(item.id);
                  const hasChildren = !!item.children?.length;
                  const isActive = isActiveRoute(item.path, item.children);
                  const isParentActive = hasChildren && isActive;

                  return (
                    <div key={item.id}>
                      <button
                        type="button"
                        onClick={() =>
                          hasChildren
                            ? toggleExpand(item.id)
                            : handleNavigation(item.path)
                        }
                        className={`
                          group w-full flex items-center justify-between
                          rounded-2xl px-2.5 py-2.5
                          text-[13px] font-semibold
                          transition-all duration-200 cursor-pointer
                          ${
                            isActive && !hasChildren
                              ? `${accent.activeBg} ${accent.activeText}`
                              : isParentActive
                                ? "bg-white/8 text-white"
                                : "text-slate-300 hover:bg-white/7 hover:text-white"
                          }
                        `}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div
                            className={`
                              flex h-[30px] w-[30px] shrink-0 items-center justify-center
                              rounded-xl transition-colors duration-200
                              ${
                                isActive && !hasChildren
                                  ? accent.iconActiveBg
                                  : "bg-white/7 group-hover:bg-white/12"
                              }
                            `}
                          >
                            {Icon && (
                              <Icon
                                className={`w-[15px] h-[15px] ${
                                  isActive && !hasChildren
                                    ? accent.iconActiveText
                                    : "text-slate-400 group-hover:text-white"
                                }`}
                              />
                            )}
                          </div>

                          <span className="truncate">{item.label}</span>
                        </div>

                        <div className="flex items-center gap-1.5 shrink-0 ml-2">
                          {item.badge !== undefined && (
                            <span
                              className={`
                                px-[7px] py-[2px]
                                text-[10px] font-bold rounded-full
                                ${
                                  isActive && !hasChildren
                                    ? "bg-white/20 text-white"
                                    : "bg-white/8 text-slate-400"
                                }
                              `}
                            >
                              {item.badge}
                            </span>
                          )}

                          {hasChildren && (
                            <ChevronDown
                              className={`w-3.5 h-3.5 text-slate-500 transition-transform duration-200 ${
                                isExpanded ? "rotate-180" : ""
                              }`}
                            />
                          )}
                        </div>
                      </button>

                      {/* Children */}
                      {hasChildren && isExpanded && (
                        <div className="mt-1 ml-5 pl-3 border-l border-white/10 space-y-1 pb-1">
                          {item.children!.map((child) => {
                            const isChildActive =
                              location.pathname === child.path;

                            return (
                              <button
                                key={child.id}
                                type="button"
                                onClick={() => handleNavigation(child.path)}
                                className={`
                                  w-full text-left rounded-xl px-3 py-2
                                  text-[12.5px] transition-all duration-200
                                  flex items-center gap-2
                                  ${
                                    isChildActive
                                      ? `${accent.childActiveBg} ${accent.childActiveText} font-semibold`
                                      : "text-slate-400 hover:bg-white/7 hover:text-white font-medium"
                                  }
                                `}
                              >
                                <span
                                  className={`w-[5px] h-[5px] rounded-full shrink-0 transition-colors ${
                                    isChildActive ? accent.dot : "bg-slate-600"
                                  }`}
                                />

                                {child.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {searchQuery && Object.keys(groupedMenu).length === 0 && (
          <div className="py-10 text-center">
            <p className="text-[13px] text-slate-500">No results for</p>
            <p className="text-[13px] text-slate-300 font-semibold mt-0.5">
              "{searchQuery}"
            </p>
          </div>
        )}
      </nav>

      {/* User footer */}
      <div
        ref={userMenuRef}
        className="relative px-3 pb-4 pt-3 border-t border-white/10 shrink-0"
      >
        {user && (
          <>
            <button
              onClick={() => setShowUserMenu((value) => !value)}
              className="
                w-full flex items-center gap-3
                rounded-2xl
                px-3 py-3
                bg-white/7 border border-white/10
                hover:bg-white/10 hover:border-white/15
                transition-all duration-200
                text-left
              "
            >
              <div className="relative shrink-0">
                <div
                  className="
                    flex h-10 w-10 items-center justify-center
                    rounded-full
                    bg-gradient-to-br from-emerald-400 to-emerald-600
                    text-white text-[14px] font-bold
                    shadow-md shadow-emerald-950/40
                    select-none
                  "
                >
                  {userInitials}
                </div>

                <span
                  className="
                    absolute bottom-0 right-0
                    h-3 w-3 rounded-full
                    bg-emerald-400 border-2 border-slate-950
                  "
                />
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-[13.5px] font-bold text-white truncate leading-tight">
                  {userDisplayName}
                </p>

                {userEmail && (
                  <p className="text-[11.5px] text-slate-400 truncate mt-0.5 leading-tight">
                    {userEmail}
                  </p>
                )}
              </div>

              <svg
                className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${
                  showUserMenu ? "rotate-180" : ""
                }`}
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 6l4 4 4-4" />
              </svg>
            </button>

            {showUserMenu && (
              <div
                className="
                  absolute left-3 right-3 bottom-full mb-3
                  overflow-hidden rounded-3xl
                  bg-slate-900 border border-white/10
                  shadow-2xl shadow-black/40
                  z-50
                "
              >
                <div
                  className="
                    flex items-center gap-3
                    px-4 py-4
                    bg-gradient-to-r from-emerald-500/15 to-cyan-500/10
                    border-b border-white/10
                  "
                >
                  <div className="relative shrink-0">
                    <div
                      className="
                        flex h-11 w-11 items-center justify-center
                        rounded-full
                        bg-gradient-to-br from-emerald-400 to-emerald-600
                        text-white text-[15px] font-bold
                        select-none
                      "
                    >
                      {userInitials}
                    </div>

                    <span
                      className="
                        absolute bottom-0 right-0
                        h-3 w-3 rounded-full
                        bg-emerald-400 border-2 border-slate-900
                      "
                    />
                  </div>

                  <div className="min-w-0">
                    <p className="text-[13.5px] font-bold text-white truncate leading-tight">
                      {userDisplayName}
                    </p>

                    {userEmail && (
                      <p className="text-[11.5px] text-slate-400 truncate mt-0.5">
                        {userEmail}
                      </p>
                    )}
                  </div>
                </div>

                {onLogout && (
                  <div className="p-2">
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        onLogout();
                      }}
                      className="
                        w-full flex items-center gap-3
                        px-3.5 py-2.5 rounded-2xl
                        text-left text-[13px] font-semibold
                        text-red-300
                        hover:bg-red-500/10 hover:text-red-200
                        transition-colors duration-200
                      "
                    >
                      <svg
                        className="w-[15px] h-[15px] shrink-0"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M6 2H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" />
                        <path d="M11 11l3-3-3-3" />
                        <path d="M14 8H6" />
                      </svg>
                      Log out
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <p className="mt-3 text-[10.5px] text-slate-600 text-center select-none">
          Quantum ISP © {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
};

export default Sidebar;
